import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase/config";
import { useCmsAuth } from "../../context/CmsAuthContext";
import { Field, SaveBar, inputClass, textareaClass } from "../cmsApi.jsx";
import {
  CLIENTE_ORIGEN,
  createEmptyCliente,
  normalizeEmail,
  normalizeEmpresa,
  normalizePhone,
} from "../../lib/crmSchema";

export default function CmsClientsEditor() {
  const { profile, user, isVendedor } = useCmsAuth();
  const [items, setItems] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(createEmptyCliente());
  const [editingId, setEditingId] = useState(null);

  const canAssign = !isVendedor;

  const loadVendedores = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where("role", "==", "vendedor"))
      );
      setVendedores(
        snap.docs
          .map((d) => {
            const data = d.data() || {};
            return {
              uid: d.id,
              email: String(data.email || ""),
              displayName: String(data.displayName || ""),
              status: String(data.status || "active"),
            };
          })
          .filter((v) => v.status === "active")
          .sort((a, b) => a.email.localeCompare(b.email))
      );
    } catch {
      setVendedores([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let q;
      if (profile?.role === "vendedor" && user?.uid) {
        q = query(
          collection(db, "clientes"),
          where("assignedVendedorId", "==", user.uid),
          orderBy("empresa", "asc")
        );
      } else {
        q = query(collection(db, "clientes"), orderBy("empresa", "asc"));
      }
      const snap = await getDocs(q);
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err?.message || "No se pudieron cargar clientes.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.role, user?.uid]);

  useEffect(() => {
    load();
    if (canAssign) loadVendedores();
  }, [load, canAssign, loadVendedores]);

  const onEdit = (row) => {
    setEditingId(row.id);
    setForm(
      createEmptyCliente({
        ...row,
        contactoNombre: row.contactoNombre || "",
      })
    );
    setMessage("");
    setError("");
  };

  const onNew = () => {
    setEditingId(null);
    setForm(
      createEmptyCliente({
        assignedVendedorId: isVendedor ? user?.uid || "" : "",
        assignedVendedorEmail: isVendedor ? user?.email || profile?.email || "" : "",
        origen: CLIENTE_ORIGEN.MANUAL,
      })
    );
    setMessage("");
    setError("");
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const emailNorm = normalizeEmail(form.email);
      if (!emailNorm.includes("@")) {
        throw new Error("Email del cliente requerido.");
      }
      let assignedVendedorId = form.assignedVendedorId;
      let assignedVendedorEmail = form.assignedVendedorEmail;
      if (isVendedor) {
        assignedVendedorId = user.uid;
        assignedVendedorEmail = user.email || profile?.email || "";
      } else if (assignedVendedorId) {
        const v = vendedores.find((x) => x.uid === assignedVendedorId);
        assignedVendedorEmail = v?.email || assignedVendedorEmail;
      }
      if (!assignedVendedorId) {
        throw new Error("Asigne una vendedora.");
      }

      const payload = {
        ...createEmptyCliente({
          ...form,
          emailNorm,
          telefonoNorm: normalizePhone(form.telefono),
          empresaNorm: normalizeEmpresa(form.empresa),
          assignedVendedorId,
          assignedVendedorEmail,
        }),
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        const { totalLeads, ...rest } = payload;
        await updateDoc(doc(db, "clientes", editingId), rest);
        setMessage("Cliente actualizado.");
      } else {
        await addDoc(collection(db, "clientes"), {
          ...payload,
          totalLeads: 0,
          createdAt: serverTimestamp(),
          lastContactAt: null,
          createdBy: user?.uid || null,
        });
        setMessage("Cliente creado.");
      }
      await load();
      onNew();
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!canAssign) return;
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try {
      await deleteDoc(doc(db, "clientes", id));
      setMessage("Cliente eliminado.");
      await load();
      if (editingId === id) onNew();
    } catch (err) {
      setError(err?.message || "No se pudo eliminar.");
    }
  };

  const title = useMemo(
    () => (isVendedor ? "Mis clientes" : "Clientes"),
    [isVendedor]
  );

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">{title}</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Fichas comerciales asignadas a vendedoras. Las consultas y cotizaciones del sitio se
          vinculan aquí.{" "}
          <Link to="/admin/leads" className="text-brand-primary font-bold underline">
            Ver leads
          </Link>
        </p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-lg">{editingId ? "Editar cliente" : "Nuevo cliente"}</h3>
          {editingId && (
            <button type="button" onClick={onNew} className="text-xs font-bold text-brand-primary">
              Cancelar edición
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Empresa">
            <input
              className={inputClass}
              value={form.empresa}
              onChange={(e) => setForm((p) => ({ ...p, empresa: e.target.value }))}
            />
          </Field>
          <Field label="Email *">
            <input
              className={inputClass}
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            />
          </Field>
          <Field label="Teléfono">
            <input
              className={inputClass}
              value={form.telefono}
              onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))}
            />
          </Field>
          <Field label="Contacto">
            <input
              className={inputClass}
              value={form.contactoNombre}
              onChange={(e) => setForm((p) => ({ ...p, contactoNombre: e.target.value }))}
            />
          </Field>
          <Field label="NIT (opcional)">
            <input
              className={inputClass}
              value={form.nit}
              onChange={(e) => setForm((p) => ({ ...p, nit: e.target.value }))}
            />
          </Field>
          {canAssign ? (
            <Field label="Vendedora *">
              <select
                className={inputClass}
                value={form.assignedVendedorId}
                onChange={(e) => {
                  const uid = e.target.value;
                  const v = vendedores.find((x) => x.uid === uid);
                  setForm((p) => ({
                    ...p,
                    assignedVendedorId: uid,
                    assignedVendedorEmail: v?.email || "",
                  }));
                }}
              >
                <option value="">Seleccione…</option>
                {vendedores.map((v) => (
                  <option key={v.uid} value={v.uid}>
                    {v.displayName || v.email} ({v.email})
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="Vendedora">
              <input className={inputClass} disabled value={form.assignedVendedorEmail || user?.email || ""} />
            </Field>
          )}
        </div>
        <Field label="Notas">
          <textarea
            className={textareaClass}
            rows={3}
            value={form.notas}
            onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
          />
        </Field>
        <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Listado ({items.length})</h3>
          <button type="button" onClick={load} className="text-xs font-bold text-brand-primary">
            Actualizar
          </button>
        </div>
        {loading && <p className="text-sm text-brand-dark/50">Cargando…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-brand-dark/50">Aún no hay clientes.</p>
        )}
        <ul className="space-y-2">
          {items.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand-soft px-4 py-3"
            >
              <div>
                <p className="font-bold text-sm">{row.empresa || row.email}</p>
                <p className="text-xs text-brand-dark/55">
                  {row.email} · {row.telefono || "sin tel."} · leads: {row.totalLeads || 0}
                  {canAssign ? ` · ${row.assignedVendedorEmail || "sin vendedora"}` : ""}
                </p>
              </div>
              <div className="flex gap-3">
                <Link
                  to={`/admin/leads?cliente=${row.id}`}
                  className="text-xs font-bold text-brand-primary"
                >
                  Ver leads
                </Link>
                <button type="button" onClick={() => onEdit(row)} className="text-xs font-bold">
                  Editar
                </button>
                {canAssign && (
                  <button
                    type="button"
                    onClick={() => onDelete(row.id)}
                    className="text-xs font-bold text-red-600"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
